import { EFlashcardState } from '@constants';
import { IFlashcard, IFlashcardFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

interface IStore {
  sort: Record<string, 'asc' | 'desc'>;
  setSort: (sort: Record<string, 'asc' | 'desc'>) => void;
  filters: IFlashcardFilters;
  setFilters: (filters: IFlashcardFilters) => void;
  showFinishCollectionModal: boolean;
  setShowFinishCollectionModal: (showFinishCollectionModal: boolean) => void;
}

const store = createStore<IStore>(set => ({
  sort: { createdAt: 'desc' },
  setSort: (sort: Record<string, 'asc' | 'desc'>) => set(state => ({ ...state, sort })),
  filters: {},
  setFilters: (filters: IFlashcardFilters) =>
    set(state => ({
      ...state,
      filters
    })),
  showFinishCollectionModal: false,
  setShowFinishCollectionModal: (showFinishCollectionModal: boolean) =>
    set(state => ({ ...state, showFinishCollectionModal }))
}));

const FlashCardCollectionService = sdk.getService('FlashcardCollectionService');

export const useMyFlashCardListByCollection = (collectionId?: string) => {
  const {
    sort,
    setSort,
    filters,
    setFilters,
    showFinishCollectionModal,
    setShowFinishCollectionModal
  } = store();
  const queryKey = ['flashcards', 'flashcards-by-collection', sort, filters, collectionId];

  const { isLoading, data } = useQuery({
    queryKey,
    queryFn: () =>
      FlashCardCollectionService.getFlashcardsByCollectionId(sort, filters, collectionId!),
    enabled: !!collectionId
  });

  const visibleFilteredFlashcardsList = useMemo(() => {
    return data?.flashcards?.filter(flashcard => !flashcard.isHidden);
  }, [data]);

  useEffect(() => {
    const allLearned =
      data?.flashcards?.every(
        flashcard => !flashcard.isHidden && flashcard.state === EFlashcardState.I_KNOW_IT
      ) && data?.flashcards?.length === data.totalVisibleFlashcards?.length;
    setShowFinishCollectionModal(allLearned ?? false);
  }, [data]);

  return {
    isLoading,
    flashcardList: data?.flashcards ?? ([] as IFlashcard[]),
    flashcardCollectionCourse: data?.course?.name,
    flashcardCollection: data ?? null,
    flashcardCollectionTitle: data?.title,
    flashcardCollectionCourseId: data?.course?._id,
    flashcardCollectionSections: data?.sections,
    totalVisibleFlashcards: data?.totalVisibleFlashcards,
    totalCorrectFlashcards: data?.totalCorrectFlashcards,
    totalWrongFlashcards: data?.totalWrongFlashcards,
    totalNotPracticedFlashcards: data?.totalNotPracticedFlashcards,
    totalMorePracticeFlashcards: data?.totalMorePracticeFlashcards,
    filteredFlashcardsList: visibleFilteredFlashcardsList ?? ([] as IFlashcard[]),
    visibleFlashcardsList: data?.totalVisibleFlashcards ?? ([] as IFlashcard[]),
    sort,
    setSort,
    filters,
    setFilters,
    showFinishCollectionModal,
    setShowFinishCollectionModal
  };
};
